import { useMemo } from 'react';
import { DEFAULT_CALLBACKS } from '../context';

/**
 * Builds the `ModalContext` value the tabs read. It is memoized so `memo()`-wrapped consumers
 * (ItemsEditTab) can skip renders; a fresh value on every parent render would bust the memo.
 *
 * Call it above the shell's `if (!order) return null`: a conditional hook breaks React's hook order.
 *
 * @param {object} parts Everything the shell wires together. `state` keys and `callbacks` keys are the
 * public `ModalContext` shape, so adding one here adds it for every tab.
 * @returns {{ state: object, callbacks: object }} The context value.
 */
export const useModalContextValue = ({
    skus,
    clone,
    serviceCatalog,
    serviceCatalogError,
    serviceCatalogLoading,
    serviceCatalogShipperId,
    tabs,
    options,
    initTab,
    initTabOffset,
    validation: { hasError, hasWarning, adminHasError, hasErrorTab },
    errors,
    isDirty,
    createSingleOrderManifest,
    setCreateSingleOrderManifest,
    sendCarrierAssignedEmail,
    setSendCarrierAssignedEmail,
    savedCustomer,
    setSaveCustomer,
    noteInfo,
    setNoteInfo,
    callbacks,
    modalCallbacks,
    onSavedCustomerSelect,
    deleteSavedCustomer,
    refetchOrder,
    runOwnWrite,
    refreshServiceCatalogs,
}) =>
    useMemo(
        () => ({
            state: {
                skus,
                order: clone,
                serviceCatalog,
                serviceCatalogError,
                serviceCatalogLoading,
                // The shipper whose catalog was loaded (the order's, else the user's) — the one a
                // quick add writes to and refreshes.
                serviceCatalogShipperId,
                tabs,
                options,
                initTab,
                initTabOffset,
                hasError,
                hasWarning,
                adminHasError,
                hasErrorTab,
                errors,
                isDirty,
                createSingleOrderManifest,
                setCreateSingleOrderManifest,
                sendCarrierAssignedEmail,
                setSendCarrierAssignedEmail,
                savedCustomer,
                setSaveCustomer,
                noteInfo,
            },
            callbacks: {
                ...DEFAULT_CALLBACKS,
                ...callbacks,
                ...modalCallbacks,
                onSavedCustomerSelect,
                deleteSavedCustomer,
                setNoteInfo,
                // After the spread on purpose: it wraps the caller's `refetchOrder` rather than
                // being replaced by it.
                refetchOrder,
                runOwnWrite,
                refreshServiceCatalogs,
            },
        }),
        [
            skus,
            clone,
            serviceCatalog,
            serviceCatalogError,
            serviceCatalogLoading,
            serviceCatalogShipperId,
            tabs,
            options,
            initTab,
            initTabOffset,
            hasError,
            hasWarning,
            adminHasError,
            hasErrorTab,
            errors,
            isDirty,
            createSingleOrderManifest,
            setCreateSingleOrderManifest,
            sendCarrierAssignedEmail,
            setSendCarrierAssignedEmail,
            savedCustomer,
            setSaveCustomer,
            noteInfo,
            setNoteInfo,
            callbacks,
            modalCallbacks,
            onSavedCustomerSelect,
            deleteSavedCustomer,
            refetchOrder,
            runOwnWrite,
            refreshServiceCatalogs,
        ]
    );
